/**
 * Repair Engine - Executes fix plans against detected project issues.
 * All schema/page/workflow mutations route through _getSchemaBridge().mutateSchema("system:repair").
 * Applies incremental steps with snapshot/rollback safety.
 * Server-safe: no direct Zustand dependency.
 */

import type { DetectedIssue } from "@/ai/selfHealing/runtimeMonitor";
import type { FixPlan, FixStep } from "@/ai/selfHealing/fixPlanner";
import type { SchemaField } from "@/core/schema/nuvraSchema";
import { logger } from "@/system/logger";
import {
  SELF_HEALING_EXECUTION_ENABLED,
  REPAIR_GOVERNANCE,
  isRegenerationLocked,
} from "@/ai/selfHealing/governanceConfig";
// Type-only - erased at compile time; breaks circular dep:
// projectStore → triggerEngine → autoHealRunner → orchestrator → repairEngine → schemaBridge → projectStore
import type * as SchemaBridgeModule from "@/core/schema/schemaBridge";

/** Injected by tests only - bypasses the require() which would re-trigger the circular dep. */
let _injectedSchemaBridgeForTest: typeof SchemaBridgeModule | null = null;

/** Test-only: inject schema bridge stubs. Call with null to restore production path. */
export function _injectSchemaBridgeForTest(bridge: typeof SchemaBridgeModule | null): void {
  _injectedSchemaBridgeForTest = bridge;
}

// Lazily resolved at call-time, not module-init time, to avoid the circular dependency
function _getSchemaBridge(): typeof SchemaBridgeModule {
  if (_injectedSchemaBridgeForTest) return _injectedSchemaBridgeForTest;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require("@/core/schema/schemaBridge");
}
import { eventBus } from "@/runtime/eventBus";

// 🔥 OPTIONAL (AI FIXER HOOK)
import { fixBuildErrors } from "@/ai/selfHealing/aiFixer";

/* =========================================================
   SERVER-SAFE PROJECT STATE
   ========================================================= */

interface RepairStoreState {
  schema:    any[];
  pages:     string[];
  workflows: any[];
  files:     any[];
  projectId?: string;

  // File mutator - always used (files are not in NuvraSchema)
  updateFiles?:     (f: any[]) => void;

  getSnapshot?:    () => any;
  pushSnapshot?:   () => void;
  restoreSnapshot?:(snap: any) => void;
  setDirty?:       (d: boolean) => void;
}

/** Injected by tests only - bypasses the window+require guard. Null in production. */
let _injectedStoreForTest: RepairStoreState | null = null;

/** Test-only: inject a RepairStoreState directly. Call with null to restore production path. */
export function _injectProjectStateForTest(state: RepairStoreState | null): void {
  _injectedStoreForTest = state;
}

const _serverStore: RepairStoreState = {
  schema:          [],
  pages:           [],
  workflows:       [],
  files:           [],
  projectId:       undefined,
  updateFiles:     () => {},
  getSnapshot:     () => null,
  pushSnapshot:    () => {},
  restoreSnapshot: () => {},
  setDirty:        () => {},
};

function getProjectState(): RepairStoreState {
  if (_injectedStoreForTest) return _injectedStoreForTest;
  if (typeof window !== "undefined") {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { useProjectStore } = require("@/store/projectStore");
      return useProjectStore.getState() as RepairStoreState;
    } catch { /* fall through */ }
  }
  return _serverStore;
}

/* =========================================================
   FIELD TYPE MAPPER
   ========================================================= */

function toSchemaFieldType(raw: string): SchemaField["type"] {
  switch (raw) {
    case "uuid":
    case "text":
    case "string":    return "string";
    case "datetime":
    case "timestamp": return "date";
    case "int":
    case "integer":
    case "float":
    case "decimal":
    case "number":    return "number";
    case "bool":
    case "boolean":   return "boolean";
    case "email":     return "email";
    case "url":       return "url";
    case "enum":      return "enum";
    case "json":      return "json";
    case "reference": return "reference";
    default:          return "string";
  }
}

/* =========================================================
   REPAIR METRICS
   ========================================================= */

let _repairMutationCount = 0;
let _repairRollbackCount = 0;

export function getRepairMetrics(): { repairMutations: number; repairRollbacks: number } {
  return { repairMutations: _repairMutationCount, repairRollbacks: _repairRollbackCount };
}

export function _resetRepairMetricsForTest(): void {
  _repairMutationCount = 0;
  _repairRollbackCount = 0;
}

/* =========================================================
   TYPES
   ========================================================= */

export interface RepairResult {
  issueId:           string;
  applied:           boolean;
  description:       string;
  stepsCompleted:    number;
  failedStep?:       string;
  rolledBack?:       boolean;
  durationMs?:       number;
  /**
   * Set when execution is blocked by governance.
   * "SELF_HEALING_EXECUTION_ENABLED=false" or "REGENERATION_LOCK_ACTIVE".
   */
  blockedBy?:        string;
  /** Phase 3C: canonical fingerprint before repair steps were applied. */
  fingerprintBefore?: string;
  /** Phase 3C: canonical fingerprint after repair steps were applied. */
  fingerprintAfter?:  string;
  /** Phase 3C: true when all mutations routed through _getSchemaBridge().mutateSchema(). */
  canonicalRouted?:   boolean;
}

export interface RepairBatchResult {
  totalIssues:   number;
  repaired:      number;
  failed:        number;
  skipped:       number;
  results:       RepairResult[];
  durationMs:    number;
}

/* =========================================================
   CONFIG
   ========================================================= */

const MAX_STEP_TIME    = 10_000;
const MAX_REPAIR_TIME  = 60_000;

/* =========================================================
   UTILITIES
   ========================================================= */

async function safeExecute<T>(fn: () => Promise<T> | T): Promise<T | null> {
  try {
    return await Promise.resolve(fn());
  } catch (err) {
    logger.error("❌ Step execution failed:", err);
    return null;
  }
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeout: ReturnType<typeof setTimeout>;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(
      () => reject(new Error(`⏱️ Step timeout after ${timeoutMs}ms`)),
      timeoutMs
    );
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeout!);
  }
}

function validateFiles(files: any[]): boolean {
  if (!Array.isArray(files)) return false;
  return files.every(
    f =>
      f &&
      typeof f.path === "string" &&
      f.path.length > 0 &&
      typeof f.content === "string"
  );
}

/* =========================================================
   STEP EXECUTION LAYER
   All mutations route through _getSchemaBridge().mutateSchema("system:repair").
   ensureSchemaInitialized() is called in executeRepair() before any steps run.
   ========================================================= */

async function executeStep(
  step:  FixStep,
  issue: DetectedIssue,
  store: RepairStoreState,
): Promise<void> {
  const govClass = REPAIR_GOVERNANCE[step.action] ?? "advisory_only";

  logger.info(`[RepairEngine] Step ${step?.order ?? "?"}: ${step?.action} [${govClass}]`);

  if (govClass === "blocked") {
    logger.warn(`[RepairEngine] Step blocked by governance: ${step.action} - manual review required`);
    return; // advisory no-op - step counted as completed
  }

  switch (step.action) {

    /* ── CREATE MODEL ── */
    case "create_model": {
      const result = _getSchemaBridge().mutateSchema("system:repair", (schema) => {
        if (!schema.models.find(m => m.name === step.target)) {
          schema.models.push({
            id:        `model-${Date.now()}`,
            name:      step.target,
            fields:    [
              { name: "id",         type: "string", required: true  },
              { name: "created_at", type: "date",   required: false },
              { name: "updated_at", type: "date",   required: false },
            ],
            relations: [],
          });
        }
      }, "system", `Repair: create model ${step.target}`);
      if (!result.success) {
        throw new Error(`Repair mutation blocked: ${result.issues?.[0] ?? "unknown"}`);
      }
      _repairMutationCount++;
      break;
    }

    /* ── UPDATE MODEL ── */
    case "update_model": {
      const result = _getSchemaBridge().mutateSchema("system:repair", (schema) => {
        const model = schema.models.find(m => issue.title?.includes(m.name));
        if (model && !model.fields.some(f => f.name === "id")) {
          model.fields.unshift({ name: "id", type: "string", required: true });
        }
      }, "system", `Repair: update model (add id field)`);
      if (!result.success) {
        throw new Error(`Repair mutation blocked: ${result.issues?.[0] ?? "unknown"}`);
      }
      _repairMutationCount++;
      break;
    }

    /* ── ADD FIELD TO MODEL ── */
    case "add_field": {
      const metaField = step.meta?.field;
      if (!metaField) break;

      const result = _getSchemaBridge().mutateSchema("system:repair", (schema) => {
        const model = schema.models.find(m => m.name === step.target);
        if (model && !model.fields.some(f => f.name === metaField.name)) {
          model.fields.push({
            name:     metaField.name,
            type:     toSchemaFieldType(metaField.type),
            required: false,
            ...(metaField.indexed !== undefined && { indexed: metaField.indexed }),
          });
        }
      }, "system", `Repair: add field ${metaField.name} to ${step.target}`);
      if (!result.success) {
        throw new Error(`Repair mutation blocked: ${result.issues?.[0] ?? "unknown"}`);
      }
      _repairMutationCount++;
      break;
    }

    /* ── CREATE PAGE ── */
    case "create_page": {
      const result = _getSchemaBridge().mutateSchema("system:repair", (schema) => {
        if (!schema.pages.find(p => p.name === step.target)) {
          schema.pages.push({
            id:           `page_${step.target.toLowerCase().replace(/\s+/g, "_")}`,
            name:         step.target,
            route:        `/${step.target.toLowerCase().replace(/\s+/g, "-")}`,
            componentIds: [],
          });
        }
      }, "system", `Repair: create page ${step.target}`);
      if (!result.success) {
        throw new Error(`Repair mutation blocked: ${result.issues?.[0] ?? "unknown"}`);
      }
      _repairMutationCount++;
      break;
    }

    /* ── UPDATE PAGE ── */
    case "update_page": {
      // advisory_only - renderer concern, no canonical mutation
      logger.info(`[RepairEngine] Page update for "${step.target}" - queued for AI renderer`);
      break;
    }

    /* ── DELETE PAGE ── */
    case "delete_page": {
      const result = _getSchemaBridge().mutateSchema("system:repair", (schema) => {
        const idx = schema.pages.findIndex(p => p.name === step.target);
        if (idx !== -1) schema.pages.splice(idx, 1);
      }, "system", `Repair: delete page ${step.target}`);
      if (!result.success) {
        throw new Error(`Repair mutation blocked: ${result.issues?.[0] ?? "unknown"}`);
      }
      _repairMutationCount++;
      break;
    }

    /* ── CREATE WORKFLOW ── */
    case "create_workflow": {
      const result = _getSchemaBridge().mutateSchema("system:repair", (schema) => {
        if (!schema.workflows.find(w => w.name === step.target)) {
          schema.workflows.push({
            id:      `workflow_${step.target.toLowerCase().replace(/\s+/g, "_")}`,
            name:    step.target,
            trigger: { type: "manual" },
            steps:   [],
          });
        }
      }, "system", `Repair: create workflow ${step.target}`);
      if (!result.success) {
        throw new Error(`Repair mutation blocked: ${result.issues?.[0] ?? "unknown"}`);
      }
      _repairMutationCount++;
      break;
    }

    /* ── LINK WORKFLOW ── */
    case "link_workflow": {
      // advisory_only - page-binding concern, no canonical mutation
      logger.info(`[RepairEngine] Workflow "${step.target}" link - registered for page binding`);
      break;
    }

    /* ── INSTALL DEPENDENCY ── */
    case "install_dependency": {
      // advisory_only - build concern, no canonical mutation
      logger.info(`[RepairEngine] Dependency suggested: ${step.target} - see nuvrahq.com/docs/dependencies`);
      break;
    }

    /* ── AI CODE FIXER ── */
    case "fix_code": {
      // Files are not in NuvraSchema - always routed via store.updateFiles()
      if (step.meta?.useAiFixer) {
        const files = store.files || [];

        const fixedFiles = await withTimeout(
          fixBuildErrors(files, step.meta.error || issue.description),
          MAX_STEP_TIME
        );

        if (!validateFiles(fixedFiles)) {
          throw new Error("Invalid AI fix output - files failed validation");
        }

        store.updateFiles?.(fixedFiles);
      }
      break;
    }

    /* ── ADD AUTH GUARD ── */
    case "add_auth_guard": {
      // advisory_only - middleware concern, no canonical mutation
      logger.info(`[RepairEngine] Auth guard for "${step.target}" - flagged for middleware injection`);
      break;
    }

    /* ── ADD RATE LIMIT ── */
    case "add_rate_limit": {
      // advisory_only - API layer concern, no canonical mutation
      logger.info(`[RepairEngine] Rate limit for "${step.target}" - flagged for API layer`);
      break;
    }

    /* ── MANUAL REVIEW ── */
    case "manual_review": {
      // blocked - already handled at top of function, but guarded here for exhaustiveness
      logger.warn(`[RepairEngine] Manual review required: ${step.target}`);
      break;
    }

    /* ── UNKNOWN ── */
    default: {
      logger.warn(`[RepairEngine] Unknown action: ${step.action}`);
      break;
    }
  }
}

/* =========================================================
   CORE ENGINE
   ========================================================= */

/**
 * Execute a repair plan for a single detected issue.
 * Ensures schema is initialized via ensureSchemaInitialized() before executing steps.
 * Always snapshots before applying and rolls back canonically on failure.
 */
export async function executeRepair(
  issue: DetectedIssue,
  plan:  FixPlan
): Promise<RepairResult> {
  const startTime = Date.now();

  // ── GOVERNANCE GATE (backstop) ────────────────────────────────────────────
  if (!SELF_HEALING_EXECUTION_ENABLED) {
    logger.warn("[RepairEngine] Execution blocked - SELF_HEALING_EXECUTION_ENABLED = false");
    return {
      issueId:        issue?.id || "unknown",
      applied:        false,
      description:    "Execution suspended - governance constraint active",
      stepsCompleted: 0,
      blockedBy:      "SELF_HEALING_EXECUTION_ENABLED=false",
      durationMs:     Date.now() - startTime,
    };
  }

  // ── REGENERATION LOCK CHECK ────────────────────────────────────────────────
  if (isRegenerationLocked()) {
    logger.warn("[RepairEngine] Execution blocked - regeneration lock active");
    return {
      issueId:        issue?.id || "unknown",
      applied:        false,
      description:    "Repair deferred - scoped regeneration lock active",
      stepsCompleted: 0,
      blockedBy:      "REGENERATION_LOCK_ACTIVE",
      durationMs:     Date.now() - startTime,
    };
  }

  const store = getProjectState();

  const initializedSchema = _getSchemaBridge().ensureSchemaInitialized();
  if (!initializedSchema) {
    return {
      issueId:        issue?.id || "unknown",
      applied:        false,
      description:    "Repair deferred - schema not initialized (no projectId)",
      stepsCompleted: 0,
      blockedBy:      "SCHEMA_UNAVAILABLE",
      durationMs:     Date.now() - startTime,
    };
  }

  if (!issue?.autoFixable) {
    return {
      issueId:        issue?.id || "unknown",
      applied:        false,
      description:    "Issue requires manual intervention - cannot auto-fix",
      stepsCompleted: 0,
      durationMs:     Date.now() - startTime,
    };
  }

  logger.info(`[RepairEngine] Starting repair for "${issue.title}"`);

  // Capture pre-repair canonical schema for lossless rollback (Phase 3C)
  const preRepairSchema = JSON.parse(JSON.stringify(_getSchemaBridge().getCurrentSchema()));
  const fingerprintBefore = preRepairSchema?.fingerprint ?? "";

  // Safety snapshot before any mutation
  const snapshot = store.getSnapshot?.();
  store.pushSnapshot?.();

  let completed = 0;

  try {
    const steps = plan?.steps || [];

    await withTimeout(
      (async () => {
        for (const step of steps) {
          if (!step?.action) {
            logger.warn("[RepairEngine] Skipping invalid step - missing action");
            continue;
          }

          const result = await safeExecute(() =>
            withTimeout(executeStep(step, issue, store), MAX_STEP_TIME)
          );

          if (result === null) {
            throw new Error(`Step failed: ${step.action} → ${step.target}`);
          }

          completed++;
          logger.info(`[RepairEngine] ✅ Step ${completed}/${steps.length} complete`);
        }
      })(),
      MAX_REPAIR_TIME
    );

    // ── POST-REPAIR SCHEMA VALIDATION (requirement 4) ──────────────────────
    const postSchema = _getSchemaBridge().getCurrentSchema();
    if (!postSchema?.projectId) {
      throw new Error("Schema lost projectId after repair - corrupt state detected");
    }

    const fingerprintAfter = postSchema.fingerprint ?? "";
    store.setDirty?.(true);

    logger.info(`[RepairEngine] Repair complete - ${completed}/${plan.steps.length} steps applied`);

    // Emit repair applied event for observability
    eventBus.emit({
      type:              "selfHealing:repair_applied",
      projectId:         store.projectId ?? "",
      issueId:           issue.id,
      stepsApplied:      completed,
      fingerprintBefore,
      fingerprintAfter,
    });

    return {
      issueId:          issue.id,
      applied:          true,
      description:      `Applied ${completed} fix steps for "${issue.title}"`,
      stepsCompleted:   completed,
      rolledBack:       false,
      durationMs:       Date.now() - startTime,
      fingerprintBefore,
      fingerprintAfter,
      canonicalRouted:  true,
    };

  } catch (err: any) {
    logger.error("[RepairEngine] ❌ Repair failed", { error: err?.message });

    // ── CANONICAL ROLLBACK (Phase 3C) ──────────────────────────────────────
    // 1. Restore flat store state from pre-repair snapshot
    if (snapshot && store.restoreSnapshot) {
      store.restoreSnapshot(snapshot);
      logger.warn("[RepairEngine] 🔁 Flat store rolled back to safe snapshot");
    }

    // 2. Restore canonical schema via the Phase 3B undo path
    if (preRepairSchema) {
      eventBus.emit({
        type:             "schema:undo_canonical_available",
        projectId:        preRepairSchema.projectId,
        schema:           preRepairSchema,
        isRepairRollback: true,
      });
      // 3. Emit staleness - bridge will consume _pendingCanonicalFromUndo flag, skip rebuild
      eventBus.emit({
        type:      "schema:staleness_detected",
        reason:    "undo",
        projectId: preRepairSchema.projectId,
      });
    }

    // 4. Emit rollback event for observability
    eventBus.emit({
      type:              "selfHealing:repair_rolled_back",
      projectId:         store.projectId ?? "",
      issueId:           issue.id,
      canonicalRestored: !!preRepairSchema,
    });
    _repairRollbackCount++;

    return {
      issueId:          issue.id,
      applied:          false,
      description:      `Repair failed after ${completed} steps - rolled back to safe state`,
      stepsCompleted:   completed,
      failedStep:       err?.message,
      rolledBack:       true,
      durationMs:       Date.now() - startTime,
      fingerprintBefore,
      canonicalRouted:  true,
    };
  }
}

/**
 * Execute repairs for multiple issues in sequence.
 * Stops on critical failures, continues on minor ones.
 */
export async function executeRepairBatch(
  items: Array<{ issue: DetectedIssue; plan: FixPlan }>
): Promise<RepairBatchResult> {
  const startTime = Date.now();
  const results:   RepairResult[] = [];
  let repaired     = 0;
  let failed       = 0;
  let skipped      = 0;

  for (const { issue, plan } of items) {
    if (!issue.autoFixable) {
      skipped++;
      results.push({
        issueId:        issue.id,
        applied:        false,
        description:    "Skipped - requires manual intervention",
        stepsCompleted: 0,
      });
      continue;
    }

    const result = await executeRepair(issue, plan);
    results.push(result);

    if (result.applied) {
      repaired++;
    } else {
      failed++;
      // Halt batch on critical issue failure
      if (issue.severity === "critical") {
        logger.error("[RepairEngine] Critical repair failed - halting batch");
        break;
      }
    }
  }

  return {
    totalIssues: items.length,
    repaired,
    failed,
    skipped,
    results,
    durationMs:  Date.now() - startTime,
  };
}

/**
 * Dry-run a repair plan - validates steps without mutating state.
 */
export function validateRepairPlan(plan: FixPlan): {
  valid:    boolean;
  issues:   string[];
} {
  const issues: string[] = [];

  if (!plan?.steps || plan.steps.length === 0) {
    issues.push("Plan has no steps");
  }

  const validActions = new Set([
    "create_model", "update_model", "add_field",
    "create_page", "update_page", "delete_page",
    "create_workflow", "link_workflow",
    "install_dependency", "fix_code",
    "add_auth_guard", "add_rate_limit",
    "manual_review",
  ]);

  for (const step of plan?.steps ?? []) {
    if (!step.action) {
      issues.push(`Step ${step.order ?? "?"} has no action`);
    } else if (!validActions.has(step.action)) {
      issues.push(`Step ${step.order}: unknown action "${step.action}"`);
    }
    if (!step.target) {
      issues.push(`Step ${step.order ?? "?"} has no target`);
    }
  }

  return { valid: issues.length === 0, issues };
}
